import React, { useEffect, useState } from 'react';
import '@wix/design-system/styles.global.css';
import { type OnWidgetAction } from '@wix/chat-widget-common-widgets';
import s from './WithDisabledWidget.module.scss';
import { Box, Text, TextButton } from '@wix/design-system';

const enabledTimeout = 1000;
interface WithDisabledWidgetProps {
  children: React.ReactNode;
  onWidgetAction: OnWidgetAction;
  widgetName?: string;
  shouldBeDisabled: boolean;
}

enum WidgetState {
  Default,
  Disabled,
  ReadyToRevive,
  Revived,
}

export const WithDisabledWidget: React.FC<WithDisabledWidgetProps> = ({
  children,
  onWidgetAction,
  widgetName,
  shouldBeDisabled,
}) => {
  const widgetRef = React.useRef<HTMLDivElement>(null);
  const overlayRef = React.useRef<HTMLDivElement>(null);
  const [widgetState, setWidgetState] = useState(
    shouldBeDisabled ? WidgetState.Disabled : WidgetState.Default,
  );

  useEffect(() => {
    if (!shouldBeDisabled) {
      setWidgetState(WidgetState.Default);
    } else {
      setWidgetState(WidgetState.Disabled);
    }
  }, [shouldBeDisabled]);

  const reviveWidget = () => {
    setWidgetState(WidgetState.Revived);
    onWidgetAction(widgetName!, { action: 'revive_widget' });
    overlayRef.current!.style.display = 'none';
    widgetRef.current!.style.pointerEvents = 'all';
    widgetRef.current!.onmouseleave = () => {
      setTimeout(disableWidget, enabledTimeout);
    };
  };

  const disableWidget = () => {
    widgetRef.current!.style.pointerEvents = 'none';
    overlayRef.current!.style.display = 'flex';
    setWidgetState(WidgetState.Disabled);
  };

  return (
    <Box className={s.container} ref={widgetRef}>
      {widgetState !== WidgetState.Default && (
        <div
          ref={overlayRef}
          className={s.overlay}
          style={{
            opacity: widgetState !== WidgetState.ReadyToRevive ? '0' : '1',
          }}
          onClick={() => {
            if (widgetState === WidgetState.Disabled) {
              setWidgetState(WidgetState.ReadyToRevive);
            }
          }}
          onMouseLeave={() => {
            widgetState !== WidgetState.Revived &&
              setTimeout(disableWidget, enabledTimeout);
          }}
        >
          {widgetState === WidgetState.ReadyToRevive && (
            <Box
              direction="vertical"
              align="center"
              className={s.enableMessage}
            >
              <Text size="tiny">
                The contents here may no longer be relevant and may cause
                unwanted results
              </Text>
              <TextButton size="tiny" onClick={reviveWidget}>
                Enable anyway
              </TextButton>
            </Box>
          )}
        </div>
      )}

      <Box
        className={
          widgetState === WidgetState.Revived ||
          widgetState === WidgetState.Default
            ? s.enabled
            : s.disabled
        }
      >
        {children}
      </Box>
    </Box>
  );
};
